'use client';

import { useId, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { tw } from '@tw';
import { MIN_BUNDLE_PRICE } from '@peach/types/bundles/types.js';
import { BundleType, type BundleDraft } from '@/components/products/bundle-config';
import { formatPrice } from '@peach/types/utils/currencies.js';
import { dealPrice } from '@/components/products/pricing';
import { useBundlesStore } from '@/components/products/use-bundles-store';

/**
 * A product's settings under its card: how many calls, how long, its price
 * (what the owner gets, and nothing else moves it), and whether it shows an old
 * price above it. The title is typed into on the card.
 */
export function ProductEditor({ bundle }: { bundle: BundleDraft }) {
  const setTerms = useBundlesStore((s) => s.setTerms);
  const remove = useBundlesStore((s) => s.remove);
  const saved = useBundlesStore((s) => s.before?.find((b) => b.id === bundle.id));
  const isTrial = bundle.type === BundleType.TRIAL;
  const was = formatPrice({
    amount: dealPrice(bundle.deal ? bundle : saved, bundle.price),
    currency: 'USD',
  });

  return (
    <div className="flex flex-col gap-4 pt-1">
      {!isTrial && (
        <NumberField
          label="Calls"
          unit="calls"
          value={bundle.numberOfClasses}
          min={1}
          max={50}
          step={1}
          onCommit={(numberOfClasses) => setTerms(bundle.id, { numberOfClasses })}
        />
      )}
      <NumberField
        label="Length"
        unit="min"
        value={bundle.duration}
        min={isTrial ? 10 : 15}
        max={isTrial ? 60 : 180}
        step={1}
        onCommit={(duration) => setTerms(bundle.id, { duration })}
      />

      <NumberField
        label="Price"
        prefix="$"
        value={bundle.price}
        min={MIN_BUNDLE_PRICE}
        max={10000}
        step={isTrial ? 1 : 5}
        onCommit={(price) => setTerms(bundle.id, { price })}
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-p-3 font-semibold text-[var(--sf-ink)]">Make it a deal</span>
          <span className="text-badge text-[var(--sf-mut)]">Shows {was} crossed out</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={bundle.deal}
          aria-label="Make it a deal"
          onClick={() => setTerms(bundle.id, { deal: !bundle.deal })}
          className={tw(
            'relative h-8 w-14 shrink-0 rounded-full transition-colors',
            bundle.deal ? 'bg-[var(--sf-ink)]' : 'bg-[var(--sf-line)]'
          )}
        >
          <span
            className={tw(
              'absolute left-1 top-1 size-6 rounded-full bg-[var(--sf-card)] transition-transform',
              bundle.deal && 'translate-x-6'
            )}
          />
        </button>
      </div>

      {/* Not on the intro: a page keeps its intro call. */}
      {!isTrial && (
        <button
          type="button"
          onClick={() => remove(bundle.id)}
          className="mm-btn min-h-11 self-start text-p-3 font-semibold text-[var(--sf-mut)] underline underline-offset-4"
        >
          Remove from my page
        </button>
      )}
    </div>
  );
}

/**
 * A number the owner types or steps. Held as text while typed and committed on
 * leaving the field or Enter, so a half-typed "1" of "15" never reprices the card.
 */
function NumberField({
  label,
  unit,
  prefix,
  value,
  min,
  max,
  step,
  onCommit,
}: {
  label: string;
  unit?: string;
  prefix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (next: number) => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const commit = () => {
    if (draft === null) return;
    // Rounded rather than stripped of its point: "19.99" is $20, never $1999.
    const typed = Math.round(parseFloat(draft.replace(/[^\d.]/g, '')));
    setDraft(null);
    if (Number.isFinite(typed) && clamp(typed) !== value) onCommit(clamp(typed));
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={id} className="text-p-3 font-semibold text-[var(--sf-mut)]">
        {label}
      </label>
      <div className="flex items-center gap-1.5">
        <StepButton
          label={`${label}: less`}
          disabled={value <= min}
          onClick={() => onCommit(clamp(value - step))}
        >
          <Minus className="size-4" />
        </StepButton>
        <span className="flex min-w-24 items-baseline justify-center gap-1 rounded-xl bg-[var(--sf-page)] px-2.5 py-1.5">
          {prefix && <span className="text-p-3 font-semibold text-[var(--sf-mut)]">{prefix}</span>}
          <input
            id={id}
            inputMode="numeric"
            value={draft ?? String(value)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setDraft(null);
            }}
            size={Math.max(String(draft ?? value).length, 2)}
            className="sf-num bg-transparent text-right text-head-2 font-extrabold text-[var(--sf-ink)] outline-none"
          />
          {unit && <span className="text-p-3 font-semibold text-[var(--sf-mut)]">{unit}</span>}
        </span>
        <StepButton
          label={`${label}: more`}
          disabled={value >= max}
          onClick={() => onCommit(clamp(value + step))}
          ink
        >
          <Plus className="size-4" />
        </StepButton>
      </div>
    </div>
  );
}

function StepButton({
  label,
  disabled,
  ink = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  ink?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={tw(
        'mm-btn flex size-11 items-center justify-center rounded-full disabled:opacity-35',
        ink
          ? 'bg-[var(--sf-ink)] text-[var(--sf-page)]'
          : 'bg-[var(--sf-page)] text-[var(--sf-ink)]'
      )}
    >
      {children}
    </button>
  );
}
